import { useEffect, useState } from "react";
import { KeyRound, Loader2, LogOut, Trash2, User, X } from "lucide-react";
import * as dialog from "@zag-js/dialog";
import { Portal, normalizeProps, useMachine } from "@zag-js/react";
import { useAuth } from "./AuthContext";

export default function AccountManager() {
  const { user, changePassword, updateName, deleteAccount, logout } = useAuth();
  const [name, setName] = useState("");
  const [nameNote, setNameNote] = useState("");
  const [delPassword, setDelPassword] = useState("");
  const [delConfirm, setDelConfirm] = useState("");
  const [delNote, setDelNote] = useState("");
  const [open, setOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const service = useMachine(dialog.machine, {
    id: "blog-account-manager",
    open,
    onOpenChange: ({ open: nextOpen }) => setOpen(nextOpen)
  });
  const api = dialog.connect(service, normalizeProps);

  useEffect(() => {
    if (open && user) setName(user.name || "");
    if (!open) {
      setNameNote("");
      setDelPassword("");
      setDelConfirm("");
      setDelNote("");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setBusy(false);
      setError("");
      setSuccess("");
    }
  }, [open, user]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (newPassword !== confirmPassword) {
      setError("The new passwords do not match.");
      return;
    }

    setBusy(true);
    const result = await changePassword({ currentPassword, newPassword });
    setBusy(false);
    if (!result.ok) {
      setError(result.error || "Could not change password.");
      return;
    }

    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setSuccess("Password changed.");
  }

  async function handleName(event) {
    event.preventDefault();
    setNameNote("");
    const result = await updateName(name);
    setNameNote(result.ok ? "Saved." : result.error);
  }

  async function handleDelete(event) {
    event.preventDefault();
    setDelNote("");
    if (delConfirm !== "DELETE") { setDelNote("Type DELETE to confirm."); return; }
    const result = await deleteAccount({ password: delPassword });
    if (result.ok) { setOpen(false); return; }
    setDelNote(result.error);
  }

  async function handleLogout() {
    await logout();
    setOpen(false);
  }

  if (!user) return null;

  return (
    <>
      <button
        {...api.getTriggerProps()}
        type="button"
        data-testid="blog-account-trigger"
        className="inline-flex items-center gap-1.5 rounded-full"
        style={{
          fontSize: 11,
          letterSpacing: "0.04em",
          padding: "6px 10px",
          border: "0.5px solid var(--blog-border-primary)",
          background: "var(--blog-bg-secondary)",
          color: "var(--blog-text-secondary)",
          cursor: "pointer",
          fontFamily: "inherit"
        }}
        aria-haspopup="dialog"
      >
        <User className="w-3 h-3" />
        <span className="hidden sm:inline">{user.name || user.email}</span>
      </button>

      {api.open && (
        <Portal>
          <div {...api.getBackdropProps()} style={{ ...api.getBackdropProps().style, zIndex: 9999 }} />
          <div {...api.getPositionerProps()} style={{ ...api.getPositionerProps().style, zIndex: 10000, padding: 16 }}>
            <div
              {...api.getContentProps()}
              className="blog-theme"
              data-blog-theme={typeof localStorage !== "undefined" ? localStorage.getItem("mc_blog_theme") || "light" : "light"}
              data-testid="blog-account-dialog"
              style={{
                width: "min(100%, 430px)",
                maxHeight: "calc(100vh - 32px)",
                overflowY: "auto",
                background: "var(--blog-bg-primary)",
                border: "1px solid var(--blog-border-primary)",
                borderRadius: 14,
                padding: 24,
                color: "var(--blog-text-primary)",
                boxShadow: "0 24px 80px rgba(0,0,0,.25)"
              }}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-mono uppercase" style={{ fontSize: 10, letterSpacing: "0.14em", color: "var(--blog-text-tertiary)" }}>Account</p>
                  <h2 className="mt-2 font-display" style={{ fontSize: 24, color: "var(--blog-text-primary)" }}>Manage your access</h2>
                </div>
                <button {...api.getCloseTriggerProps()} type="button" aria-label="Close account manager" style={{ background: "transparent", border: 0, color: "var(--blog-text-tertiary)", cursor: "pointer" }}>
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="mt-5 rounded-lg" style={{ border: "1px solid var(--blog-border-secondary)", padding: 14 }}>
                <p style={{ fontSize: 13, color: "var(--blog-text-primary)" }}>{user.name || "Journal reader"}</p>
                <p className="mt-1" style={{ fontSize: 12, color: "var(--blog-text-tertiary)" }}>{user.email}</p>
              </div>

              <form onSubmit={handleName} className="mt-6 space-y-3">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" style={{ color: "var(--blog-accent-teal)" }} />
                  <h3 style={{ fontSize: 14, color: "var(--blog-text-primary)" }}>Your name</h3>
                </div>
                <div className="flex gap-2">
                  <input value={name} onChange={(event) => setName(event.target.value)} maxLength={80} required aria-label="Your name" className="w-full rounded-md" style={{ border: "1px solid var(--blog-border-primary)", background: "var(--blog-bg-secondary)", color: "var(--blog-text-primary)", padding: "9px 10px" }} />
                  <button type="submit" className="rounded-md" style={{ padding: "9px 14px", border: 0, background: "var(--blog-text-primary)", color: "var(--blog-bg-primary)", cursor: "pointer" }}>Save</button>
                </div>
                {nameNote && <p role="status" style={{ fontSize: 12, color: "var(--blog-text-secondary)" }}>{nameNote}</p>}
              </form>

              <form onSubmit={handleSubmit} className="mt-6 space-y-3">
                <div className="flex items-center gap-2">
                  <KeyRound className="w-4 h-4" style={{ color: "var(--blog-accent-teal)" }} />
                  <h3 style={{ fontSize: 14, color: "var(--blog-text-primary)" }}>Change password</h3>
                </div>
                {user.hasPassword === false && <p style={{ fontSize: 12, color: "var(--blog-text-tertiary)" }}>You signed up with Google. Set a password to sign in with your email too.</p>}
                {user.hasPassword !== false && <label className="block" style={{ fontSize: 12, color: "var(--blog-text-secondary)" }}>
                  Current password
                  <input value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} type="password" autoComplete="current-password" required className="mt-1 w-full rounded-md" style={{ border: "1px solid var(--blog-border-primary)", background: "var(--blog-bg-secondary)", color: "var(--blog-text-primary)", padding: "9px 10px" }} />
                </label>}
                <label className="block" style={{ fontSize: 12, color: "var(--blog-text-secondary)" }}>
                  New password
                  <input value={newPassword} onChange={(event) => setNewPassword(event.target.value)} type="password" autoComplete="new-password" minLength={8} required className="mt-1 w-full rounded-md" style={{ border: "1px solid var(--blog-border-primary)", background: "var(--blog-bg-secondary)", color: "var(--blog-text-primary)", padding: "9px 10px" }} />
                </label>
                <label className="block" style={{ fontSize: 12, color: "var(--blog-text-secondary)" }}>
                  Confirm new password
                  <input value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} type="password" autoComplete="new-password" minLength={8} required className="mt-1 w-full rounded-md" style={{ border: "1px solid var(--blog-border-primary)", background: "var(--blog-bg-secondary)", color: "var(--blog-text-primary)", padding: "9px 10px" }} />
                </label>

                {error && <p role="alert" style={{ fontSize: 12, color: "#C0264D" }}>{error}</p>}
                {success && <p role="status" style={{ fontSize: 12, color: "var(--blog-accent-teal)" }}>{success}</p>}

                <button type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-md" style={{ width: "100%", padding: "10px 14px", border: 0, background: "var(--blog-text-primary)", color: "var(--blog-bg-primary)", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}>
                  {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  {busy ? "Saving..." : "Change password"}
                </button>
              </form>

              <button type="button" onClick={handleLogout} className="mt-5 inline-flex items-center gap-2" style={{ background: "transparent", border: 0, color: "var(--blog-text-tertiary)", cursor: "pointer", padding: 0, fontSize: 12 }}>
                <LogOut className="w-3.5 h-3.5" />
                Sign out
              </button>

              <form onSubmit={handleDelete} className="mt-6 space-y-3 rounded-lg" style={{ border: "1px solid #C0264D55", padding: 14 }}>
                <div className="flex items-center gap-2">
                  <Trash2 className="w-4 h-4" style={{ color: "#C0264D" }} />
                  <h3 style={{ fontSize: 14, color: "var(--blog-text-primary)" }}>Delete account</h3>
                </div>
                <p style={{ fontSize: 12, color: "var(--blog-text-tertiary)" }}>This removes your account and any progress saved from Forma. It can't be undone.</p>
                {user.hasPassword !== false && (
                  <input value={delPassword} onChange={(event) => setDelPassword(event.target.value)} type="password" autoComplete="current-password" placeholder="Your password" aria-label="Your password" className="w-full rounded-md" style={{ border: "1px solid var(--blog-border-primary)", background: "var(--blog-bg-secondary)", color: "var(--blog-text-primary)", padding: "9px 10px" }} />
                )}
                <input value={delConfirm} onChange={(event) => setDelConfirm(event.target.value)} placeholder="Type DELETE" aria-label="Type DELETE to confirm" className="w-full rounded-md" style={{ border: "1px solid var(--blog-border-primary)", background: "var(--blog-bg-secondary)", color: "var(--blog-text-primary)", padding: "9px 10px" }} />
                {delNote && <p role="alert" style={{ fontSize: 12, color: "#C0264D" }}>{delNote}</p>}
                <button type="submit" className="rounded-md" style={{ width: "100%", padding: "10px 14px", border: 0, background: "#C0264D", color: "#fff", cursor: "pointer" }}>Delete my account</button>
              </form>
            </div>
          </div>
        </Portal>
      )}
    </>
  );
}
